import { useState } from 'react';
import { toHex } from '@eip/engine';
import { candidatesFor, snapGarment, type TemplateSource } from '../lib/templates';
import { colorChips, MAX_MOCKUP_COLORS, normalizeHex, removeColor, showColor, styleColorOptions, swatchList } from '../lib/mockup-ui';
import { useStore } from '../state/store';
import { topUndoToken } from '../state/undo';
import { Section } from './Section';

const NON_RECOLORABLE_TITLE = 'This blank cannot be recoloured';

/**
 * The "Garment colours" section: the style's own blanks as pressable swatches
 * (front view — colour is a style-wide, not a view-specific, choice; see
 * `garmentForColors`), plus a swatch for any selected colour none of them cover
 * (`swatchList`'s `custom` entries — a hand-typed or once-snapped-elsewhere hex),
 * then the CHOSEN colourways as named chips below, each with the × that removes
 * it, and last a custom-hex add row that snaps to a blank the same way
 * `garmentForColors` will when the colour is committed.
 * Owns its `.section`/`<h3>` the way `InkList`/`WhitePanel`/... do, so
 * `MockupPanel` mounts it directly with no extra wrapper.
 */
export function ColorSwatches({ sources, style, colors, recolorable, onChange }: {
  sources: TemplateSource[];
  style: string;
  colors: string[];
  /** Whether the CURRENT template (not necessarily every blank the style has)
   *  can be recoloured — a non-recolourable current blank can't preview an
   *  arbitrary typed colour, so the add row is disabled (its own stock
   *  swatches, which may belong to other, recolourable blanks of the style,
   *  stay clickable). */
  recolorable: boolean;
  /** `label` is what the undo step and the toast are called — the chip's × names
   *  the colour it removed, and everything else takes the default. */
  onChange: (colors: string[], label?: string) => void;
}) {
  const options = styleColorOptions(sources, style, 'front');
  const swatches = swatchList(colors, options);
  const chips = colorChips(colors, options);
  const showToast = useStore((s) => s.showToast);
  const [addHex, setAddHex] = useState('#808080');
  const normalized = normalizeHex(addHex);
  const snapped = normalized ? snapGarment(normalized, candidatesFor(sources, style, 'front')).snappedTo : null;
  const atMax = colors.length >= MAX_MOCKUP_COLORS;
  const addDisabled = !normalized || atMax || !recolorable;

  const onAdd = () => {
    if (!normalized) return;
    onChange(showColor(colors, snapped ? toHex(snapped.garmentColor) : normalized));
  };

  return (
    <Section id="colours" title="Garment colours">
      <div className="swatches">
        {swatches.map((o) => {
          const selected = colors.includes(o.hex), onScreen = o.hex === colors[0];
          return (
            // By blank, not by colour: two blanks of a style can be photographed
            // in the same colour (a duplicate of a built-in is exactly that), and
            // keying by hex gave React two children with the same key.
            <span key={o.templateId ?? o.hex} className="swatch-wrap">
              <button
                type="button"
                // `colors[0]` is the colour on screen (see `showColor`): ringed, and named so in
                // its tooltip, because "selected" and "showing" are different things here. A
                // click here only ever previews — the named chip below is what removes a colour.
                className={onScreen ? 'swatch shown' : 'swatch'}
                style={{ background: o.hex }}
                aria-pressed={selected}
                aria-current={onScreen ? 'true' : undefined}
                aria-label={o.name}
                title={onScreen ? `${o.name} (on screen)` : o.name}
                onClick={() => onChange(showColor(colors, o.hex))}
              />
            </span>
          );
        })}
      </div>
      {/* The chosen colourways, named, with the × that removes each one (audit
          #7: the × used to be a 15-px dot ON the swatch it removed, so the
          careful click that meant "show me this colour" and the careless one that
          meant "throw it away" were a few pixels apart). A click on a swatch
          above only ever previews; nothing is removed except from this list. */}
      {chips.length > 0 && (
        <div className="chips" role="list" aria-label="Chosen colours">
          {chips.map((c) => {
            const onScreen = c.hex === colors[0];
            return (
              <span key={c.hex} className={onScreen ? 'chip on-screen' : 'chip'} role="listitem" title={onScreen ? `${c.name} · on screen` : undefined}>
                <span className="chip-dot" style={{ background: c.hex }} aria-hidden="true" />
                <span className="chip-name">{c.name}</span>
                <button
                  type="button"
                  className="chip-remove"
                  aria-label={`Remove ${c.name}`}
                  // The toast is bound to the entry the removal just pushed, so
                  // its Undo brings THIS colour back however many edits land in
                  // the six seconds it is on screen.
                  onClick={() => {
                    onChange(removeColor(colors, c.hex), `Removed ${c.name}`);
                    showToast(`Removed ${c.name}`, { undoToken: topUndoToken() });
                  }}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
      {!recolorable && (
        <div className="muted small">This blank cannot be recoloured; other colours use another blank of this style</div>
      )}
      <div className="row swatch-custom">
        <input
          type="color"
          value={normalized ?? '#808080'}
          onChange={(e) => setAddHex(e.target.value)}
          aria-label="New garment colour"
        />
        <input
          value={addHex}
          onChange={(e) => setAddHex(e.target.value)}
          aria-label="New garment colour hex"
          aria-invalid={!normalized}
          style={{ width: 84 }}
        />
        <button type="button" onClick={onAdd} disabled={addDisabled} title={recolorable ? undefined : NON_RECOLORABLE_TITLE}>Add</button>
        {snapped && <span className="muted small">Snaps to {snapped.colorName}</span>}
      </div>
      {atMax && <div className="muted small">Up to 8 colours</div>}
    </Section>
  );
}
